import type { Role } from '../types'

/**
 * =============================================================================
 * CATÁLOGO DE PERMISOS
 * =============================================================================
 * Explicación completa en docs/04-permisos.md
 *
 * LA IDEA CENTRAL: el código nunca pregunta por el ROL, pregunta por la
 * CAPACIDAD.
 *
 *   ❌ if (user.role === 'admin')   → mal
 *   ✅ if (can('cash-register:close'))  → bien
 *
 * ¿Por qué? Porque los roles cambian. El día que el cliente diga "los
 * vendedores con antigüedad también deben poder cerrar caja", con el primer
 * enfoque tienes que buscar y editar cada `if` esparcido por el proyecto —
 * y siempre se escapa alguno, que además nadie nota hasta que un usuario
 * reporta que no puede hacer su trabajo.
 *
 * Con el segundo enfoque editas UNA línea de la tabla de abajo y listo.
 * El código de las pantallas no se toca nunca.
 *
 * ⚠️ RECORDATORIO DE SEGURIDAD
 * Estos permisos controlan lo que se MUESTRA, no lo que se PUEDE hacer.
 * Todo lo que corre en el navegador es modificable por el usuario: cualquiera
 * puede abrir las herramientas de desarrollo y forzar que un botón aparezca.
 * El backend DEBE validar los mismos permisos en cada endpoint. Lo de aquí es
 * experiencia de usuario (no mostrar botones que van a fallar), no seguridad.
 * =============================================================================
 */

export const PERMISSIONS = {
  // Clientes
  CUSTOMERS_VIEW: 'customers:view',
  CUSTOMERS_CREATE: 'customers:create',
  CUSTOMERS_EDIT: 'customers:edit',
  CUSTOMERS_DELETE: 'customers:delete',
  TRIP_SALES_VIEW: 'trip-sales:view',
  TRIP_SALES_CREATE: 'trip-sales:create',
  TRIP_SALES_REASSIGN: 'trip-sales:reassign-assigned-seller',
  // Comisión de Operador aplicada (F-013). Deliberadamente ausente del
  // arreglo de seller_agent: el Seller nunca debe ver este valor.
  TRIP_SALES_OPERATOR_COMMISSION_VIEW: 'trip-sales:operator-commission:view',
  // Reserva (F-005). El registro es de Admin y Seller; la corrección solo de
  // Admin. Accountant solo ve la referencia mediante `trip-sales:view`.
  TRIP_SALES_REGISTER_RESERVATION: 'trip-sales:register-reservation',
  TRIP_SALES_CORRECT_RESERVATION_REFERENCE: 'trip-sales:correct-reservation-reference',
  PAYMENTS_VIEW: 'payments:view',
  PAYMENT_RECEIPTS_VIEW: 'payment-receipts:view',
  PAYMENTS_REGISTER: 'payments:register',
  PAYMENTS_CORRECT: 'payments:correct',
  PAYMENTS_VOID: 'payments:void',
  INVENTORY_VIEW: 'inventory:view',
  INVENTORY_EDIT: 'inventory:edit',
  INVENTORY_COST_VIEW: 'inventory:cost:view',
  COMPLIMENTARY_DELIVERIES_REGISTER: 'complimentary-deliveries:register',
  PRODUCTS_VIEW: 'products:view',
  PRODUCTS_CREATE: 'products:create',
  PRODUCTS_EDIT: 'products:edit',
  PRODUCTS_COST_VIEW: 'products:cost:view',
  PRODUCTS_INVENTORY_REPLENISH: 'products:inventory:replenish',
  PRODUCT_SALES_VIEW: 'product-sales:view',
  PRODUCT_SALES_CREATE: 'product-sales:create',
  PRODUCT_PAYMENTS_REGISTER: 'product-payments:register',
  CASH_REGISTER_VIEW: 'cash-register:view',
  CASH_REGISTER_ADD_ENTRY: 'cash-register:add-entry',
  CASH_REGISTER_CLOSE: 'cash-register:close',
  DASHBOARD_VIEW: 'dashboard:view',
  REPORTS_SALES_VIEW: 'reports:sales:view',
  REPORTS_FINANCIAL_VIEW: 'reports:financial:view',
  USERS_MANAGE: 'users:manage',
  SETTINGS_EDIT: 'settings:edit',
  // Operadores (F-003)
  OPERATORS_VIEW: 'operators:view',
  OPERATORS_CREATE: 'operators:create',
  OPERATORS_EDIT: 'operators:edit',
  OPERATORS_COMMISSION_VIEW: 'operators:commission:view',
  OPERATORS_COMMISSION_CONFIGURE: 'operators:commission:configure',
  OPERATORS_STATUS_MANAGE: 'operators:status:manage',
  /**
   * Solo habilita el control de selección de Operador en el futuro formulario
   * de Venta de Viaje (F-004); no otorga autoridad de selección real — esa
   * verificación la hace el comando de Venta de Viaje en el backend.
   */
  OPERATORS_TRIP_SALE_SELECT: 'operators:trip-sale:select',
  // Viajeros (F-006)
  TRAVELERS_VIEW: 'travelers:view',
  TRAVELERS_CREATE: 'travelers:create',
  TRAVELERS_EDIT: 'travelers:edit',
  TRAVELERS_DELETE: 'travelers:delete',
  // Viaje individual (F-007)
  INDIVIDUAL_TRIPS_VIEW: 'individual-trips:view',
  INDIVIDUAL_TRIPS_CREATE: 'individual-trips:create',
  INDIVIDUAL_TRIPS_EDIT: 'individual-trips:edit',
  // Viaje grupal (F-008)
  GROUP_TRIPS_VIEW: 'group-trips:view',
  GROUP_TRIPS_CREATE: 'group-trips:create',
  GROUP_TRIPS_EDIT: 'group-trips:edit',
  GROUP_TRIPS_OPERATOR_CONFIGURE: 'group-trips:operator:configure',
  GROUP_TRIPS_ASSOCIATE_TRIP_SALE: 'group-trips:associate-trip-sale',
  // Comisión de Operador aplicada al total del Viaje Grupal (F-008/F-013).
  // Deliberadamente ausente del arreglo de seller_agent: el Seller nunca debe
  // ver este valor, igual que trip-sales:operator-commission:view.
  GROUP_TRIPS_OPERATOR_COMMISSION_VIEW: 'group-trips:operator-commission:view',
} as const

/**
 * Tipo generado automáticamente a partir del objeto de arriba.
 * Gracias a esto, escribir `can('customers:vew')` (con un typo) es un ERROR DE
 * COMPILACIÓN, no un permiso que silenciosamente siempre da false.
 */
export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS]

/**
 * =============================================================================
 * QUÉ PUEDE HACER CADA ROL
 * =============================================================================
 * 👉 ESTE ES EL ÚNICO LUGAR QUE SE EDITA CUANDO CAMBIAN LOS PERMISOS.
 *
 * Al ser un `Record<Role, ...>`, si agregas un rol nuevo al tipo `Role` y
 * olvidas definir sus permisos aquí, TypeScript no compila. Es imposible que
 * un rol quede sin configurar por descuido.
 * =============================================================================
 */
export const PERMISSIONS_BY_ROLE: Record<Role, readonly Permission[]> = {
  admin: [
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.CUSTOMERS_VIEW,
    PERMISSIONS.CUSTOMERS_CREATE,
    PERMISSIONS.CUSTOMERS_EDIT,
    PERMISSIONS.CUSTOMERS_DELETE,
    PERMISSIONS.TRIP_SALES_VIEW,
    PERMISSIONS.TRIP_SALES_CREATE,
    PERMISSIONS.TRIP_SALES_REASSIGN,
    PERMISSIONS.TRIP_SALES_OPERATOR_COMMISSION_VIEW,
    PERMISSIONS.TRIP_SALES_REGISTER_RESERVATION,
    PERMISSIONS.TRIP_SALES_CORRECT_RESERVATION_REFERENCE,
    PERMISSIONS.PAYMENTS_VIEW,
    PERMISSIONS.PAYMENT_RECEIPTS_VIEW,
    PERMISSIONS.PAYMENTS_REGISTER,
    PERMISSIONS.PAYMENTS_CORRECT,
    PERMISSIONS.PAYMENTS_VOID,
    PERMISSIONS.INVENTORY_VIEW,
    PERMISSIONS.INVENTORY_EDIT,
    PERMISSIONS.INVENTORY_COST_VIEW,
    PERMISSIONS.COMPLIMENTARY_DELIVERIES_REGISTER,
    PERMISSIONS.PRODUCTS_VIEW,
    PERMISSIONS.PRODUCTS_CREATE,
    PERMISSIONS.PRODUCTS_EDIT,
    PERMISSIONS.PRODUCTS_COST_VIEW,
    PERMISSIONS.PRODUCTS_INVENTORY_REPLENISH,
    PERMISSIONS.PRODUCT_SALES_VIEW,
    PERMISSIONS.PRODUCT_SALES_CREATE,
    PERMISSIONS.PRODUCT_PAYMENTS_REGISTER,
    PERMISSIONS.CASH_REGISTER_VIEW,
    PERMISSIONS.CASH_REGISTER_ADD_ENTRY,
    PERMISSIONS.CASH_REGISTER_CLOSE,
    PERMISSIONS.REPORTS_SALES_VIEW,
    PERMISSIONS.REPORTS_FINANCIAL_VIEW,
    PERMISSIONS.USERS_MANAGE,
    PERMISSIONS.SETTINGS_EDIT,
    PERMISSIONS.OPERATORS_VIEW,
    PERMISSIONS.OPERATORS_CREATE,
    PERMISSIONS.OPERATORS_EDIT,
    PERMISSIONS.OPERATORS_COMMISSION_VIEW,
    PERMISSIONS.OPERATORS_COMMISSION_CONFIGURE,
    PERMISSIONS.OPERATORS_STATUS_MANAGE,
    PERMISSIONS.OPERATORS_TRIP_SALE_SELECT,
    PERMISSIONS.TRAVELERS_VIEW,
    PERMISSIONS.TRAVELERS_CREATE,
    PERMISSIONS.TRAVELERS_EDIT,
    PERMISSIONS.TRAVELERS_DELETE,
    PERMISSIONS.INDIVIDUAL_TRIPS_VIEW,
    PERMISSIONS.INDIVIDUAL_TRIPS_CREATE,
    PERMISSIONS.INDIVIDUAL_TRIPS_EDIT,
    PERMISSIONS.GROUP_TRIPS_VIEW,
    PERMISSIONS.GROUP_TRIPS_CREATE,
    PERMISSIONS.GROUP_TRIPS_EDIT,
    PERMISSIONS.GROUP_TRIPS_OPERATOR_CONFIGURE,
    PERMISSIONS.GROUP_TRIPS_ASSOCIATE_TRIP_SALE,
    PERMISSIONS.GROUP_TRIPS_OPERATOR_COMMISSION_VIEW,
  ],

  /** Vende y da seguimiento a clientes. No toca caja ni ve finanzas. */
  seller_agent: [
    PERMISSIONS.CUSTOMERS_VIEW,
    PERMISSIONS.CUSTOMERS_CREATE,
    PERMISSIONS.CUSTOMERS_EDIT,
    PERMISSIONS.TRIP_SALES_VIEW,
    PERMISSIONS.TRIP_SALES_CREATE,
    PERMISSIONS.TRIP_SALES_REGISTER_RESERVATION,
    PERMISSIONS.PAYMENTS_VIEW,
    PERMISSIONS.PAYMENT_RECEIPTS_VIEW,
    PERMISSIONS.PAYMENTS_REGISTER,
    PERMISSIONS.INVENTORY_VIEW,
    PERMISSIONS.COMPLIMENTARY_DELIVERIES_REGISTER,
    PERMISSIONS.PRODUCTS_VIEW,
    PERMISSIONS.PRODUCT_SALES_VIEW,
    PERMISSIONS.PRODUCT_SALES_CREATE,
    PERMISSIONS.PRODUCT_PAYMENTS_REGISTER,
    PERMISSIONS.OPERATORS_VIEW,
    PERMISSIONS.OPERATORS_TRIP_SALE_SELECT,
    PERMISSIONS.TRAVELERS_VIEW,
    PERMISSIONS.TRAVELERS_CREATE,
    PERMISSIONS.TRAVELERS_EDIT,
    PERMISSIONS.TRAVELERS_DELETE,
    PERMISSIONS.INDIVIDUAL_TRIPS_VIEW,
    PERMISSIONS.INDIVIDUAL_TRIPS_CREATE,
    PERMISSIONS.GROUP_TRIPS_VIEW,
    PERMISSIONS.GROUP_TRIPS_CREATE,
    PERMISSIONS.GROUP_TRIPS_EDIT,
    PERMISSIONS.GROUP_TRIPS_OPERATOR_CONFIGURE,
    PERMISSIONS.GROUP_TRIPS_ASSOCIATE_TRIP_SALE,
  ],
  accountant: [
    PERMISSIONS.CUSTOMERS_VIEW,
    PERMISSIONS.TRIP_SALES_VIEW,
    PERMISSIONS.TRIP_SALES_OPERATOR_COMMISSION_VIEW,
    PERMISSIONS.PAYMENTS_VIEW,
    PERMISSIONS.PAYMENT_RECEIPTS_VIEW,
    PERMISSIONS.INVENTORY_VIEW,
    PERMISSIONS.INVENTORY_COST_VIEW,
    PERMISSIONS.PRODUCTS_VIEW,
    PERMISSIONS.PRODUCT_SALES_VIEW,
    PERMISSIONS.OPERATORS_VIEW,
    PERMISSIONS.OPERATORS_COMMISSION_VIEW,
    PERMISSIONS.TRAVELERS_VIEW,
    PERMISSIONS.INDIVIDUAL_TRIPS_VIEW,
    PERMISSIONS.GROUP_TRIPS_VIEW,
    PERMISSIONS.GROUP_TRIPS_OPERATOR_COMMISSION_VIEW,
  ],
}

/** Función pura: ¿este rol tiene este permiso? Fácil de testear. */
export function roleHasPermission(role: Role, permission: Permission): boolean {
  return PERMISSIONS_BY_ROLE[role].includes(permission)
}
